import { useEffect, useRef, useState } from "react";
import { Modal } from "../../ui/modal";
import { Transaction, Wallet as WalletType } from "../types";
import jsPDF from "jspdf";
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Copy, 
  Wallet as WalletIcon,
  ArrowDownToLine,
  ArrowUpFromLine,
  Banknote,
  CreditCard,
  Smartphone,
  User,
  Mail,
  Phone,
  Building,
  Download,
  Loader2,
  ChevronDown,
  FileText,
  Image as ImageIcon
} from "lucide-react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  wallet: WalletType | null;
}

interface DetailRow {
  label: string;
  value: string;
}

const TransactionSummary = ({ isOpen, onClose, transaction, wallet }: Props) => {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!transaction || !wallet) return null;

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const removeUnderscoreAndCapitalize = (str: string = "") => {
    if (!str) return "";
    return str
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getStatusConfig = () => {
    switch (transaction.status) {
      case "SUCCESSFUL":
        return {
          color: "text-green-600 dark:text-green-400",
          bg: "bg-green-100 dark:bg-green-900/30",
          rgb: [22, 163, 74] as [number, number, number],
          rgbBg: [220, 252, 231] as [number, number, number],
          hex: "#16a34a",
          hexBg: "#dcfce7",
          title: "Successful",
          icon: <CheckCircle className="h-4 w-4" />
        };
      case "PENDING":
        return {
          color: "text-yellow-600 dark:text-yellow-400",
          bg: "bg-yellow-100 dark:bg-yellow-900/30",
          rgb: [202, 138, 4] as [number, number, number],
          rgbBg: [254, 249, 195] as [number, number, number],
          hex: "#ca8a04",
          hexBg: "#fef9c3",
          title: "Pending",
          icon: <Clock className="h-4 w-4" />
        };
      case "FAILED":
        return {
          color: "text-red-600 dark:text-red-400",
          bg: "bg-red-100 dark:bg-red-900/30",
          rgb: [220, 38, 38] as [number, number, number],
          rgbBg: [254, 226, 226] as [number, number, number],
          hex: "#dc2626",
          hexBg: "#fee2e2",
          title: "Failed",
          icon: <XCircle className="h-4 w-4" />
        };
      case "UNKNOWN":
      default:
        return {
          color: "text-gray-600 dark:text-gray-400",
          bg: "bg-gray-100 dark:bg-gray-900/30",
          rgb: [75, 85, 99] as [number, number, number],
          rgbBg: [243, 244, 246] as [number, number, number],
          hex: "#4b5563",
          hexBg: "#f3f4f6",
          title: "Cancelled",
          icon: <XCircle className="h-4 w-4" />
        };
    }
  };

  const getTransactionIcon = () => {
    const purpose = transaction.purpose?.toLowerCase() || "";
    const type = transaction.type?.toLowerCase() || "";
    
    if (purpose.includes("fund") || purpose.includes("deposit")) {
      return <ArrowDownToLine className="h-5 w-5 text-green-600 dark:text-green-400" />;
    } else if (purpose.includes("payout") || purpose.includes("withdraw")) {
      return <ArrowUpFromLine className="h-5 w-5 text-red-600 dark:text-red-400" />;
    } else if (purpose.includes("transfer")) {
      return <ArrowDownToLine className="h-5 w-5 text-blue-600 dark:text-blue-400" />;
    } else if (type === "credit" || type === "release") {
      return <ArrowDownToLine className="h-5 w-5 text-green-600 dark:text-green-400" />;
    } else if (type === "debit" || type === "hold") {
      return <ArrowUpFromLine className="h-5 w-5 text-red-600 dark:text-red-400" />;
    } else {
      return <WalletIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getPaymentMethodIcon = (method: string = "") => {
    const methodLower = method.toLowerCase();
    
    if (methodLower.includes("bank")) {
      return <Banknote className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    } else if (methodLower.includes("card")) {
      return <CreditCard className="h-4 w-4 text-purple-600 dark:text-purple-400" />;
    } else if (methodLower.includes("mobile")) {
      return <Smartphone className="h-4 w-4 text-green-600 dark:text-green-400" />;
    } else {
      return <WalletIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };


  const loadImageAsDataURL = (url: string): Promise<{ dataUrl: string; width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL("image/png"),
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = reject;
    img.src = url;
  });
};

  const statusConfig = getStatusConfig();
  const metadata = transaction.metadata as any || {};

  // ---------- Shared data builders ----------

  const getMainRows = (): DetailRow[] => {
    const rows: DetailRow[] = [
      { label: "Reference", value: transaction.reference },
      // { label: "Transaction ID", value: transaction.id },
      { label: "Date & Time", value: `${formatDate(transaction.createdAt)} at ${formatTime(transaction.createdAt)}` },
      { label: "Type", value: removeUnderscoreAndCapitalize(transaction.type) },
    ];

    if (transaction.paymentMethod) {
      rows.push({ label: "Payment Method", value: removeUnderscoreAndCapitalize(transaction.paymentMethod) });
    }

    rows.push({ label: "Purpose", value: removeUnderscoreAndCapitalize(transaction.purpose) });

    if (transaction.narration) {
      rows.push({ label: "Narration", value: transaction.narration });
    }

    if (transaction.fee !== undefined && transaction.fee > 0) {
      rows.push({ label: "Fee", value: `${transaction.currency} ${formatNumber(transaction.fee)}` });
    }

    return rows;
  };

  const getAdditionalSection = (): { title: string; rows: DetailRow[] } | null => {
    const purpose = transaction.purpose?.toLowerCase() || "";

    if (purpose === "wallet transfer" || purpose === "trade settlement") {
      const party = transaction.type === "DEBIT" ? metadata.receiver : metadata.sender;
      const partyLabel = transaction.type === "DEBIT" ? "Receiver" : "Sender";
      const rows: DetailRow[] = [];
      if (party?.name) rows.push({ label: "Name", value: party.name });
      if (party?.email) rows.push({ label: "Email", value: party.email });
      return rows.length ? { title: `${partyLabel} Details`, rows } : null;
    }

    if (purpose === "payout") {
      const rows: DetailRow[] = [];
      const method = transaction.paymentMethod?.toLowerCase() || "";

      if (method === "mobile money") {
        if (metadata.receiver?.phoneNumber) rows.push({ label: "Phone", value: metadata.receiver.phoneNumber });
        if (metadata.receiver?.network) rows.push({ label: "Network", value: removeUnderscoreAndCapitalize(metadata.receiver.network) });
      } else if (method === "bank transfer") {
        const account = metadata.senderAccountNumber || metadata.receiver?.accountNumber;
        const accountName = metadata.senderName || metadata.receiver?.name;
        if (account) rows.push({ label: "Account", value: account });
        if (accountName) rows.push({ label: "Account Name", value: accountName });
        if (metadata.receiver?.bankName) rows.push({ label: "Bank", value: metadata.receiver.bankName });
      }

      return rows.length ? { title: "Payout Details", rows } : null;
    }

    if (purpose === "fund wallet") {
      const rows: DetailRow[] = [];
      if (metadata.paymentMethod === "MOBILE_MONEY") {
        if (metadata.phoneNumber) rows.push({ label: "Phone", value: metadata.phoneNumber });
        if (metadata.network) rows.push({ label: "Network", value: removeUnderscoreAndCapitalize(metadata.network) });
      } else {
        if (metadata.senderAccountNumber) rows.push({ label: "Account", value: metadata.senderAccountNumber });
        if (metadata.senderName) rows.push({ label: "Account Name", value: metadata.senderName });
      }
      return rows.length ? { title: "Payment Details", rows } : null;
    }

    return null;
  };

  // ---------- PDF generation ----------

  const handleDownloadPDF = async () => {
    setMenuOpen(false);
    setDownloading(true);
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 48;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;

      try {
  const logo = await loadImageAsDataURL("/images/logo/SecondaryLogoPNG.png");
  const logoMaxWidth = 120;
  const logoMaxHeight = 56;
  const ratio = Math.min(logoMaxWidth / logo.width, logoMaxHeight / logo.height);
  const logoW = logo.width * ratio;
  const logoH = logo.height * ratio;
  doc.addImage(
    logo.dataUrl,
    "PNG",
    pageWidth - margin - logoW,
    margin - 8,
    logoW,
    logoH
  );
} catch (e) {
  console.warn("Could not load logo for PDF", e);
}

      const mainRows = getMainRows();
      const additional = getAdditionalSection();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(17, 24, 39);
      doc.text("Transaction Receipt", margin, y);
      y += 22;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      doc.text(
        `${removeUnderscoreAndCapitalize(transaction.purpose)} | ${formatDate(transaction.createdAt)} at ${formatTime(transaction.createdAt)}`,
        margin,
        y
      );
      y += 32;

      const sign = transaction.type === "CREDIT" || transaction.type === "RELEASE" ? "+" : "-";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(26);
      doc.setTextColor(17, 24, 39);
      doc.text(
        `${sign}${transaction.currency} ${formatNumber(Math.abs(transaction.amount))}`,
        margin,
        y
      );
      y += 30;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      const badgeText = statusConfig.title;
      const badgeTextWidth = doc.getTextWidth(badgeText);
      const badgePaddingX = 10;
      const badgeHeight = 20;
      const badgeWidth = badgeTextWidth + badgePaddingX * 2;

      doc.setFillColor(...statusConfig.rgbBg);
      doc.roundedRect(margin, y, badgeWidth, badgeHeight, badgeHeight / 2, badgeHeight / 2, "F");
      doc.setTextColor(...statusConfig.rgb);
      doc.text(badgeText, margin + badgePaddingX, y + badgeHeight / 2 + 3.5);
      y += badgeHeight + 24;

      doc.setDrawColor(229, 231, 235);
      doc.line(margin, y, pageWidth - margin, y);
      y += 20;

      const labelSize = 10;
      const valueSize = 10;
      const lineHeight = 14;
      const rowGap = 8;

      const drawRows = (rows: DetailRow[]) => {
        rows.forEach((row) => {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(labelSize);
          doc.setTextColor(107, 114, 128);
          doc.text(row.label, margin, y);

          doc.setFont("helvetica", "normal");
          doc.setFontSize(valueSize);
          doc.setTextColor(17, 24, 39);
          const wrapped: string[] = doc.splitTextToSize(row.value, contentWidth * 0.55);
          wrapped.forEach((line, i) => {
            const lineWidth = doc.getTextWidth(line);
            doc.text(line, pageWidth - margin - lineWidth, y + i * lineHeight);
          });

          y += Math.max(1, wrapped.length) * lineHeight + rowGap;

          if (y > doc.internal.pageSize.getHeight() - 100) {
            doc.addPage();
            y = margin;
          }
        });
      };

      drawRows(mainRows);
      y += 12;

      if (additional) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(55, 65, 81);
        doc.text(additional.title, margin, y);
        y += 20;
        drawRows(additional.rows);
        y += 12;
      }

      doc.setDrawColor(229, 231, 235);
      doc.line(margin, y, pageWidth - margin, y);
      y += 20;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `${wallet.name || "Wallet"} | Generated on ${new Date().toLocaleString("en-US")}`,
        margin,
        y
      );
      y += 12;
      doc.text("This is a system-generated receipt.", margin, y);

      doc.save(`receipt-${transaction.reference}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  // ---------- PNG generation (Canvas) ----------

  const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
  ): string[] => {
    const words = text.split(" ");
    const lines: string[] = [];
    let current = "";

    words.forEach((word) => {
      const test = current ? `${current} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    });
    if (current) lines.push(current);
    return lines.length ? lines : [""];
  };

  const handleDownloadPNG = async () => {
    setMenuOpen(false);
    setDownloading(true);
    try {
      const width = 640;
      const padding = 40;
      const contentWidth = width - padding * 2;
      const labelFont = "600 13px Arial";
      const valueFont = "400 13px Arial";
      const lineHeight = 20;
      const rowGap = 6;
      const sectionGap = 28;

    const logoImg = new Image();
     logoImg.crossOrigin = "anonymous";
     const logoLoaded = new Promise<boolean>((resolve) => {
       logoImg.onload = () => resolve(true);
       logoImg.onerror = () => resolve(false);
       logoImg.src = "/images/logo/SecondaryLogoPNG.png";
     });
     const hasLogo = await logoLoaded;

      const measureCanvas = document.createElement("canvas");
      const mctx = measureCanvas.getContext("2d")!;

      const mainRows = getMainRows();
      const additional = getAdditionalSection();

      const measureRowsHeight = (rows: DetailRow[]) => {
        let h = 0;
        rows.forEach((row) => {
          mctx.font = valueFont;
          const wrapped = wrapText(mctx, row.value, contentWidth * 0.6);
          h += Math.max(1, wrapped.length) * lineHeight + rowGap;
        });
        return h;
      };

      let totalHeight = padding;
      totalHeight += 90;
      totalHeight += 90;
      totalHeight += 20;
      totalHeight += measureRowsHeight(mainRows);
      totalHeight += sectionGap;

      if (additional) {
        totalHeight += 30;
        totalHeight += measureRowsHeight(additional.rows);
        totalHeight += sectionGap;
      }

      totalHeight += 80;
      totalHeight += padding;

      const canvas = document.createElement("canvas");
      const scale = 2;
      canvas.width = width * scale;
      canvas.height = totalHeight * scale;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${totalHeight}px`;
      const ctx = canvas.getContext("2d")!;
      ctx.scale(scale, scale);

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, totalHeight);

      let y = padding;

      if (hasLogo) {
       const logoMaxWidth = 120;
       const logoMaxHeight = 56;
       const ratio = Math.min(logoMaxWidth / logoImg.naturalWidth, logoMaxHeight / logoImg.naturalHeight);
       const logoW = logoImg.naturalWidth * ratio;
       const logoH = logoImg.naturalHeight * ratio;
       ctx.drawImage(logoImg, width - padding - logoW, y, logoW, logoH);
     }

      ctx.fillStyle = "#111827";
      ctx.font = "700 20px Arial";
      ctx.textBaseline = "top";
      ctx.fillText("Transaction Receipt", padding, y);
      y += 28;

      ctx.fillStyle = "#6b7280";
      ctx.font = "400 12px Arial";
      ctx.fillText(
        `${removeUnderscoreAndCapitalize(transaction.purpose)} • ${formatDate(transaction.createdAt)} at ${formatTime(transaction.createdAt)}`,
        padding,
        y
      );
      y += 40;

      ctx.fillStyle = "#111827";
      ctx.font = "700 30px Arial";
      const sign = transaction.type === "CREDIT" || transaction.type === "RELEASE" ? "+" : "-";
      ctx.fillText(
        `${sign}${transaction.currency} ${formatNumber(Math.abs(transaction.amount))}`,
        padding,
        y
      );
      y += 46;

      const badgeText = statusConfig.title;
      ctx.font = "600 12px Arial";
      const badgeTextWidth = ctx.measureText(badgeText).width;
      const badgePaddingX = 12;
      const badgeHeight = 24;
      const badgeWidth = badgeTextWidth + badgePaddingX * 2;

      const drawRoundedRect = (x: number, yy: number, w: number, h: number, r: number) => {
        ctx.beginPath();
        ctx.moveTo(x + r, yy);
        ctx.arcTo(x + w, yy, x + w, yy + h, r);
        ctx.arcTo(x + w, yy + h, x, yy + h, r);
        ctx.arcTo(x, yy + h, x, yy, r);
        ctx.arcTo(x, yy, x + w, yy, r);
        ctx.closePath();
      };

      ctx.fillStyle = statusConfig.hexBg;
      drawRoundedRect(padding, y, badgeWidth, badgeHeight, badgeHeight / 2);
      ctx.fill();
      ctx.fillStyle = statusConfig.hex;
      ctx.fillText(badgeText, padding + badgePaddingX, y + 6);
      y += badgeHeight + 24;

      ctx.strokeStyle = "#e5e7eb";
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
      y += 20;

      const drawRows = (rows: DetailRow[]) => {
        rows.forEach((row) => {
          ctx.font = labelFont;
          ctx.fillStyle = "#6b7280";
          ctx.fillText(row.label, padding, y);

          ctx.font = valueFont;
          ctx.fillStyle = "#111827";
          const wrapped = wrapText(ctx, row.value, contentWidth * 0.6);
          const valueX = width - padding;
          wrapped.forEach((line, i) => {
            const lineWidth = ctx.measureText(line).width;
            ctx.fillText(line, valueX - lineWidth, y + i * lineHeight);
          });

          y += Math.max(1, wrapped.length) * lineHeight + rowGap;
        });
      };

      drawRows(mainRows);
      y += sectionGap - rowGap;

      if (additional) {
        ctx.font = "700 13px Arial";
        ctx.fillStyle = "#374151";
        ctx.fillText(additional.title, padding, y);
        y += 26;
        drawRows(additional.rows);
        y += sectionGap - rowGap;
      }

      ctx.strokeStyle = "#e5e7eb";
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
      y += 20;

      ctx.font = "400 11px Arial";
      ctx.fillStyle = "#9ca3af";
      ctx.fillText(`${wallet.name || "Wallet"} • Generated on ${new Date().toLocaleString("en-US")}`, padding, y);
      y += 16;
      ctx.fillText("This is a system-generated receipt.", padding, y);

      const dataUrl = canvas.toDataURL("image/png", 1.0);
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `receipt-${transaction.reference}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-md rounded-2xl border border-gray-200 bg-white p-6
        dark:border-gray-800 dark:bg-gray-900"
    >
      {/* Header */}
      <div className="mb-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
              {getTransactionIcon()}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                {transaction.purpose}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatDate(transaction.createdAt)} • {formatTime(transaction.createdAt)}
              </p>
            </div>
          </div>
        </div>

        {/* Amount */}
        <div className="mb-4 text-center">
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {transaction.type === "CREDIT" || transaction.type === "RELEASE" ? "+" : "-"}
            {transaction.currency} {formatNumber(Math.abs(transaction.amount))}
          </div>
          <div className="mt-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
              {statusConfig.icon}
              {statusConfig.title}
            </span>
          </div>
        </div>
      </div>

      {/* Transaction Details */}
      <div className="space-y-2">
        {/* Reference */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-800">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Reference</div>
            <button
              onClick={() => copyToClipboard(transaction.reference)}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark transition-colors"
            >
              <Copy className="h-3 w-3" />
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="font-mono text-sm text-gray-600 dark:text-gray-400 break-all">
            {transaction.reference}
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid gap-3">
          {/* Transaction Type */}
          <div className="flex items-center justify-between py-1">
            <div className="text-sm text-gray-600 dark:text-gray-400">Type</div>
            <div className="text-sm font-medium text-gray-800 dark:text-white">
              {removeUnderscoreAndCapitalize(transaction.type)}
            </div>
          </div>

          {/* Payment Method */}
          {transaction.paymentMethod && (
            <div className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                {getPaymentMethodIcon(transaction.paymentMethod)}
                <span>Payment Method</span>
              </div>
              <div className="text-sm font-medium text-gray-800 dark:text-white">
                {removeUnderscoreAndCapitalize(transaction.paymentMethod)}
              </div>
            </div>
          )}

          {/* Purpose */}
          <div className="flex items-center justify-between py-1">
            <div className="text-sm text-gray-600 dark:text-gray-400">Purpose</div>
            <div className="text-sm font-medium text-gray-800 dark:text-white">
              {removeUnderscoreAndCapitalize(transaction.purpose)}
            </div>
          </div>

          {/* Narration */}
          {transaction.narration && (
            <div className="flex items-center justify-between py-1">
              <div className="text-sm text-gray-600 dark:text-gray-400">Narration</div>
              <div className="text-sm font-medium text-gray-800 dark:text-white text-right">
                {transaction.narration}
              </div>
            </div>
          )}

          {/* Fee */}
          {transaction.fee !== undefined && transaction.fee > 0 && (
            <div className="flex items-center justify-between py-1">
              <div className="text-sm text-gray-600 dark:text-gray-400">Fee</div>
              <div className="text-sm font-medium text-gray-800 dark:text-white">
                {transaction.currency} {formatNumber(transaction.fee)}
              </div>
            </div>
          )}
        </div>

        {/* Additional Details based on Purpose */}
        {(transaction.purpose?.toLowerCase() === "wallet transfer" ||
          transaction.purpose?.toLowerCase() === "trade settlement") && metadata && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-800">
            <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              {transaction.type === "DEBIT" ? "Receiver" : "Sender"} Details
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600 dark:text-gray-400">Name:</span>
                <span className="font-medium text-gray-800 dark:text-white">
                  {transaction.type === "DEBIT" ? metadata.receiver?.name : metadata.sender?.name}
                </span>
              </div>

              {(transaction.type === "DEBIT" ? metadata.receiver?.email : metadata.sender?.email) && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">Email:</span>
                  <span className="font-medium text-gray-800 dark:text-white">
                    {transaction.type === "DEBIT" ? metadata.receiver?.email : metadata.sender?.email}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {transaction.purpose?.toLowerCase() === "payout" && metadata && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-800">
            <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              Payout Details
            </h3>
            <div className="space-y-2">
              {transaction.paymentMethod?.toLowerCase() === "mobile money" && (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {metadata.receiver?.phoneNumber}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Building className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Network:</span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {removeUnderscoreAndCapitalize(metadata.receiver?.network)}
                    </span>
                  </div>
                </>
              )}

              {transaction.paymentMethod?.toLowerCase() === "bank transfer" && (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <CreditCard className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Account:</span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {metadata.senderAccountNumber || metadata.receiver?.accountNumber}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Account Name:</span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {metadata.senderName || metadata.receiver?.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Building className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Bank:</span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {metadata.receiver?.bankName}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {transaction.purpose?.toLowerCase() === "fund wallet" && metadata && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-800">
            <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              Payment Details
            </h3>
            <div className="space-y-2">
              {metadata.paymentMethod === "MOBILE_MONEY" ? (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {metadata.phoneNumber}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Building className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Network:</span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {removeUnderscoreAndCapitalize(metadata.network)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <CreditCard className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Account:</span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {metadata.senderAccountNumber}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Account Name:</span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {metadata.senderName}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-5 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2
            text-sm font-medium text-gray-700 hover:bg-gray-100
            dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
        >
          Close
        </button>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((prev) => !prev)}
            disabled={downloading}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm
              font-medium text-white hover:opacity-90 transition-opacity
              flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {downloading ? "Preparing..." : "Download Receipt"}
            {!downloading && <ChevronDown className="h-3.5 w-3.5" />}
          </button>

          {menuOpen && !downloading && (
            <div
              className="absolute bottom-full right-0 mb-2 w-44 overflow-hidden
                rounded-lg border border-gray-200 bg-white shadow-lg
                dark:border-gray-700 dark:bg-gray-800 z-10"
            >
              <button
                onClick={handleDownloadPDF}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm
                  text-gray-700 hover:bg-gray-100
                  dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <FileText className="h-4 w-4 text-red-500" />
                Download as PDF
              </button>
              <button
                onClick={handleDownloadPNG}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm
                  text-gray-700 hover:bg-gray-100
                  dark:text-gray-200 dark:hover:bg-gray-700 border-t border-gray-100 dark:border-gray-700"
              >
                <ImageIcon className="h-4 w-4 text-blue-500" />
                Download as Image
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TransactionSummary;